import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import type { Hint } from "~/features/chat/types";
import { ContentCopyButton } from "./ContentCopyButton";
import { UnifiedMarkdown } from "./UnifiedMarkdown";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

interface SelectedTerm {
  term: string;
  definition: string;
}

interface TermDefinitionDialogProps {
  /** The container element to attach event delegation to */
  containerRef: HTMLElement | null;
  /** Map of lowercased term → definition for O(1) lookup */
  termMap: Map<string, string>;
}

/**
 * A single shared dialog that opens on click of `.term-highlight` marks.
 * Uses event delegation — one click listener on the container —
 * instead of mounting a component per term.
 */
export function TermDefinitionDialog({
  containerRef,
  termMap,
}: TermDefinitionDialogProps) {
  const [selected, setSelected] = useState<SelectedTerm | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!containerRef) return;

    const openTerm = (mark: HTMLElement, e: Event) => {
      const key = mark.getAttribute("data-term-key");
      if (!key) return;

      const definition = termMap.get(key);
      if (!definition) return;

      e.preventDefault();
      e.stopPropagation();
      setSelected({ term: mark.textContent ?? key, definition });
      setIsOpen(true);
    };

    const handleClick = (e: MouseEvent) => {
      const mark = (e.target as Element).closest<HTMLElement>(
        "mark.term-highlight"
      );
      if (!mark || !containerRef.contains(mark)) return;
      openTerm(mark, e);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const mark = (e.target as Element).closest<HTMLElement>(
        "mark.term-highlight"
      );
      if (!mark || !containerRef.contains(mark)) return;
      openTerm(mark, e);
    };

    containerRef.addEventListener("click", handleClick);
    containerRef.addEventListener("keydown", handleKeyDown);
    return () => {
      containerRef.removeEventListener("click", handleClick);
      containerRef.removeEventListener("keydown", handleKeyDown);
    };
  }, [containerRef, termMap]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="capitalize">{selected?.term}</DialogTitle>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="leading-snug">
            <UnifiedMarkdown>{selected?.definition ?? ""}</UnifiedMarkdown>
          </div>
        </DialogDescription>
        <DialogFooter className="sm:justify-between">
          <ContentCopyButton
            content={selected ? `${selected.term}: ${selected.definition}` : ""}
            size="sm"
            preCopyMessageId="copy"
          />
          <DialogClose asChild>
            <Button variant="outline" size="sm">
              <FormattedMessage id="common.close" defaultMessage="Close" />
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Builds an O(1) lookup map from term arrays.
 * Medications are added first, then AI terms override (higher priority).
 * Memoize this with useMemo in the consuming component.
 */
export function buildTermMap(
  terms: Hint[],
  medications?: Hint[]
): Map<string, string> {
  const map = new Map<string, string>();
  if (medications) {
    for (const m of medications) {
      map.set(m.term.toLowerCase(), m.definition);
    }
  }
  for (const t of terms) {
    map.set(t.term.toLowerCase(), t.definition);
  }
  return map;
}
